import { Injectable } from '@angular/core';
import { AuthService } from './auth.service';
import { BehaviorSubject, Observable, forkJoin, of, throwError } from 'rxjs';
import { Http2Service } from './http2.service';
import {
  getDateInString,
  tryConvertBirthdateStringToDate,
} from '@helpers/helper';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import {
  IPatientDashboardData,
  IPatientDetailsDashboardData,
  IPhysioDashboardData,
} from '../resolvers';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import {
  PatientListItem,
  PatientDataResponse,
  PaginatedPatientsResponse,
  DiseaseOption,
  PatientsFilterOptionsResponse,
} from '@schemas/patient.model';

@Injectable({
  providedIn: 'root',
})
export class StoreService {
  searches$: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  updatesSearches(searches) {
    this.searches$.next(searches);
  }

  displaySpinner: BehaviorSubject<boolean> = new BehaviorSubject(false);
  refreshData$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  createPatientProgram_DataResolver(patiendId: string): any {
    this.displaySpinner.next(true);
    this.refreshData$.next(true);
    return forkJoin({
      patientDetails: this.getUserDetails(patiendId),
      allExercisePrograms: this.getPrograms(patiendId),
      activeTreatmentDetails: this.getTreatmentProgress(patiendId),
      activeDays: this.getActiveDays(patiendId),
      longestStreak: this.getLongestStreak(patiendId),
      allExercises: this.getAllExercises(),
      programTemplates: this.getProgramTemplates(),
    }).pipe(
      tap((d) => {
        this.displaySpinner.next(false);
        this.refreshData$.next(false);
      }),
    );
  }
  patients_dashboards_data: BehaviorSubject<any> = new BehaviorSubject<any>({});
  getPatientDetailsDashboardDataResolver(
    patientId: string,
  ):
    | IPatientDetailsDashboardData
    | Observable<IPatientDetailsDashboardData>
    | Promise<IPatientDetailsDashboardData> {
    this.displaySpinner.next(true);
    let requests: any = {};
    if (this.auth.me.role == 'physiotherapist') {
      if (
        this.patients_dashboards_data.value[patientId] &&
        this.refreshData$.value == false
      ) {
        this.displaySpinner.next(false);
        return this.patients_dashboards_data.value[patientId];
      }
    }
    requests = {
      patientDetails: this.getUserDetails(patientId),
    };

    return forkJoin(requests)
      .pipe(
        catchError((e) => {
          if (e.error == 'patientNotFound') this.router.navigate(['404']);
          return throwError(e);
        }),
      )
      .pipe(
        tap((data) => {
          const updatedValue: any = { ...this.patients_dashboards_data.value };
          updatedValue[patientId] = data;
          this.patients_dashboards_data.next(updatedValue);
        }),
      )
      .pipe(
        tap((data) => {
          this.displaySpinner.next(false);
          this.refreshData$.next(false);
        }),
      );
  }

  getPhysioDashboardData(
    patientId: string | undefined,
    test_exercise_id: string | undefined,
  ): Observable<IPhysioDashboardData> {
    this.displaySpinner.next(true);

    if (this._physio_dashboard_data$?.value) {
      const selectedExercise = test_exercise_id
        ? this._physio_dashboard_data$.value.exercises.find(
            (e) => e._id == test_exercise_id,
          )
        : undefined;

      const updatedData = {
        ...this._physio_dashboard_data$.value,
        selectedExercise,
      };

      this._physio_dashboard_data$.next(updatedData);
      this.displaySpinner.next(false);

      return of(updatedData);
    }

    return forkJoin({
      diseases_list: this.getDiseaseOptions({
        language: this.translate.currentLang,
        screen: 'survey',
      }),
      exercises: this.getAllExercises(),
    }).pipe(
      map((data) => ({
        ...data,
        selectedExercise: test_exercise_id
          ? data.exercises.find((e) => e.id == test_exercise_id)
          : undefined,
      })),
      tap((data) => {
        this._physio_dashboard_data$.next(data);
        this.displaySpinner.next(false);
      }),
      catchError((error) => {
        this.displaySpinner.next(false);
        console.error('Error loading dashboard data:', error);
        throw error;
      }),
    );
  }
  getExerciseById(id: string) {
    return this.http.fetch('get_exercise_by_id', { exercise_id: id });
  }
  getSurveyQuestions(survey_type: string) {
    return this.http.fetch('get_survey', { survey_type });
  }
  getPatientVersions(patient_uid: string) {
    return this.http.fetch('get_patient_versions', { patient_uid });
  }
  getPatientDashboardData():
    | IPatientDashboardData
    | Observable<IPatientDashboardData>
    | Promise<IPatientDashboardData> {
    const id = this.auth.me.id;
    this.displaySpinner.next(true);
    return forkJoin({
      allExercisePrograms: this.getPrograms(id, true),
      userDetails: of(this.auth.me),
      activeTreatmentDetails: this.getTreatmentProgress(id),
      beforeSurvey: this.getSurveyQuestions('before'),
      afterSurvey: this.getSurveyQuestions('after'),
      tasks: this.getUserTasks({ patient_uid: id }),
      appointments: this.getAppointments({ patient_uid: id }),
    }).pipe(
      tap((d) => {
        this.displaySpinner.next(false);
      }),
    );
  }
  private _physio_dashboard_data$: BehaviorSubject<IPhysioDashboardData> =
    new BehaviorSubject<IPhysioDashboardData>(null);
  get physio_dashboard_data$() {
    return this._physio_dashboard_data$;
  }
  private patients_programs: BehaviorSubject<any> = new BehaviorSubject<any>(
    {},
  );
  private patients_active_days: BehaviorSubject<any> = new BehaviorSubject<any>(
    {},
  );
  private patients_longest_streak: BehaviorSubject<any> =
    new BehaviorSubject<any>({});
  private program_templates: BehaviorSubject<any[]> = new BehaviorSubject<
    any[]
  >([]);
  private diseasesList$: BehaviorSubject<any[]> = new BehaviorSubject<any[]>(
    [],
  );
  private exercises_list: BehaviorSubject<any[]> = new BehaviorSubject<any[]>(
    [],
  );
  diseasesList(): Observable<any[]> {
    return this.diseasesList$.asObservable();
  }
  constructor(
    private http: Http2Service,
    private auth: AuthService,
    private router: Router,
    private translate: TranslateService,
  ) {}
  onLogout() {
    this.displaySpinner.next(true);
    this.patients_programs.next({});
    this.patients_active_days.next({});
    this.patients_longest_streak.next({});
    this.program_templates.next([]);
    this.exercises_list.next([]);
    this._physio_dashboard_data$.next(null);

    setTimeout(() => {
      this.displaySpinner.next(false);
    }, 1000);
  }
  getUserDetails(id?: string) {
    if (!id) {
      return of(this.auth.authenticatedUser.getValue());
    }
    const reqBody: any = id ? { patient_uid: id } : {};
    return this.http.fetch('get_user_details', reqBody).pipe(
      catchError((err) => {
        return throwError(err);
      }),
    );
  }

  getPatientsDataPaginated(params: any): Observable<any> {
    const reqBody: any = {
      current_date: getDateInString(new Date()),
      language: this.translate.currentLang,
      page: params?.page ?? 1,
      page_size: params?.page_size,
    };

    if (this.hasRequestValue(params?.sort_by)) {
      reqBody.sort_by = params.sort_by;
    }

    if (this.hasRequestValue(params?.sort_order)) {
      reqBody.sort_order = params.sort_order;
    }

    const filters: any = {};
    this.addFilter(filters, 'search', params?.search);
    this.addFilter(filters, 'diseases', params?.diseases);
    this.addFilter(filters, 'activity_statuses', params?.activity_statuses);
    this.addFilter(filters, 'insurances', params?.insurances);
    this.addFilter(filters, 'coverages', params?.coverages);

    if (Object.keys(filters).length > 0) {
      reqBody.filters = filters;
    }

    const diseaseOptions = this.diseasesList$.getValue();
    const diseaseOptions$ =
      diseaseOptions?.length && !params?.refreshDiseaseOptions
        ? of(diseaseOptions)
        : this.getDiseaseOptions({
            language: this.translate.currentLang,
            screen: 'survey',
          });

    return forkJoin({
      response: this.http.fetch('v2/get_patients_data', reqBody),
      diseaseOptions: diseaseOptions$,
    }).pipe(
      map(({ response, diseaseOptions }) => {
        const payload = (response ?? {}) as Partial<PaginatedPatientsResponse>;
        const patients = Array.isArray(payload?.data) ? payload.data : [];

        return {
          patients: this.preparePatientsListWithDiseaseTitles(
            patients,
            diseaseOptions ?? [],
          ),
          total_count: payload?.total_count ?? patients.length,
          overall_total_count: payload?.overall_total_count,
          page: payload?.page ?? params?.page ?? 1,
          page_size: payload?.page_size ?? params?.page_size,
        };
      }),
    );
  }

  private hasRequestValue(value: any): boolean {
    return (
      value !== undefined &&
      value !== null &&
      value !== '' &&
      value !== 'none' &&
      (!Array.isArray(value) || value.length > 0)
    );
  }

  private addFilter(filters: any, key: string, value: any) {
    if (this.hasRequestValue(value)) {
      filters[key] = value;
    }
  }

  getPatientsFilterOptions(): Observable<PatientsFilterOptionsResponse> {
    return this.http.fetch('v2/get_patients_filter_options', {}).pipe(
      map((res) => {
        const payload = res?.data ?? res ?? {};

        return {
          diseases: Array.isArray(payload?.diseases) ? payload.diseases : [],
          activity_statuses: Array.isArray(payload?.activity_statuses)
            ? payload.activity_statuses
            : [],
          coverages: Array.isArray(payload?.coverages) ? payload.coverages : [],
          insurances: Array.isArray(payload?.insurances)
            ? payload.insurances
            : [],
        };
      }),
    );
  }

  private mapPatientDataToPatientListItem(
    patient: PatientDataResponse,
    diseaseOptions: DiseaseOption[] = [],
  ): PatientListItem {
    const daysLeft = patient.days_left ?? 0;
    const totalDays = patient.total_days ?? 0;

    return {
      ...patient,
      _id: patient.id,
      uid: patient.id,

      birthdate: tryConvertBirthdateStringToDate(patient.birthdate),
      created_at: patient.created_at ? new Date(patient.created_at) : null,

      illness: this.mapIllnessTitles(patient.illness, diseaseOptions),

      hasUnreadNoti: patient.has_unread_notification,

      last_active_date: patient.last_seen,
      last_activity_date: patient.last_seen,

      program_summary: {
        days_left: daysLeft,
        total_days: totalDays,
        done_percentage:
          totalDays > 0 ? ((totalDays - daysLeft) / totalDays) * 100 : 100,
      },
    };
  }

  private mapIllnessTitles(
    illnessIds: string[] = [],
    diseaseOptions: DiseaseOption[] = [],
  ): string {
    const diseaseTitleMap = new Map(
      diseaseOptions.map((option) => [option._id, option.title]),
    );

    return [...illnessIds]
      .sort((a, b) => a.localeCompare(b))
      .map(
        (illnessId) =>
          diseaseTitleMap.get(illnessId) ??
          this.translate.instant('generalTerms.other'),
      )
      .join(', ');
  }

  private preparePatientsListWithDiseaseTitles(
    patients: PatientDataResponse[],
    diseaseOptions: DiseaseOption[],
  ): PatientListItem[] {
    return patients.map((patient) =>
      this.mapPatientDataToPatientListItem(patient, diseaseOptions),
    );
  }

  getTreatmentProgress(patientId: string) {
    const reqBody: any = {
      patient_uid: patientId,
      current_date: getDateInString(new Date()),
    };
    return this.http
      .fetch('get_exercise_program_completion_summary', reqBody)
      .pipe(map((res) => res?.data));
  }
  checkSurveyAvailability(survey_type: string) {
    const reqBody: any = {
      survey_type: survey_type,
      current_iso_datetime: new Date().toISOString(),
    };
    return this.http
      .fetch('check_survey_availability', reqBody)
      .pipe(map((res) => res?.data));
  }

  setUserDetails(reqBody: any) {
    return this.http.fetch('set_user_details', reqBody).pipe();
  }
  getPrograms(patientId: any, force: boolean = false) {
    const all = this.patients_programs.getValue();

    if (all[patientId] && !force && this.refreshData$.value == false) {
      return of(all[patientId]);
    }

    return this.http
      .fetch('get_exercise_program', {
        current_date: getDateInString(new Date()),
        patient_uid: patientId,
      })
      .pipe(map((res) => res?.data))
      .pipe(
        tap((p) => {
          const all = this.patients_programs.getValue();
          all[patientId] = p;

          if (p.length > 0) {
            p.map((pro) => {
              pro.exercises = pro.exercises.map((e) => ({
                ...e,
                initial_pose: {
                  margin: e.initial_pose?.margin,
                  pose: e.initial_pose?.pose,
                },
              }));

              return pro;
            });
          }

          this.patients_programs.next({ ...all, [patientId]: [...p] });
        }),
      );
  }
  getProgramTemplates() {
    if (
      this.program_templates.getValue()?.length > 0 &&
      this.refreshData$.value == false
    ) {
      return of([...this.program_templates.value]);
    }
    return this.http
      .fetch('get_exercise_program_template', {})
      .pipe(map((res) => res?.data))
      .pipe(
        tap((data) => {
          const list = data.map((t) => {
            return { ...t, expanded: false, edited: false };
          });
          this.program_templates.next(list);
        }),
      );
  }
  getAllExercises(short_version: boolean = false) {
    if (this.exercises_list.getValue()?.length > 0) {
      return of(this.exercises_list.getValue());
    }
    return this.http
      .fetch(
        short_version
          ? 'get_all_exercises_names_and_categories'
          : 'get_all_exercises',
        {},
      )
      .pipe(map((res) => res?.data))
      .pipe(
        map((list) => {
          return list.filter((e) => !e.disabled);
        }),
      )
      .pipe(
        map((list) => {
          return list.map((e: any) => {
            if (e.initial_pose)
              return {
                ...e,
                initial_pose: {
                  // Preserve metadata such as the localized initial-pose instruction.
                  ...e.initial_pose,
                  margin: e.initial_pose.margin,
                  pose: e.initial_pose.pose,
                },
              };

            return { ...e, exercise_id: e._id };
          });
        }),
      )
      .pipe(
        tap((list) => {
          list.sort((a, b) => {
            const aInitialPose = a.initial_pose.pose.split('_')[1];
            const bInitialPose = b.initial_pose.pose.split('_')[1];
            if (
              ['breathing', 'breathing_time', 'relaxation'].includes(
                a.category,
              ) ||
              ['breathing', 'breathing_time', 'relaxation'].includes(b.category)
            ) {
              if (
                !['breathing', 'breathing_time', 'relaxation'].includes(
                  a.category,
                )
              ) {
                return 1;
              }
              if (
                !['breathing', 'breathing_time', 'relaxation'].includes(
                  b.category,
                )
              ) {
                return -1;
              }
              if (bInitialPose === 'sit' && aInitialPose !== 'sit') {
                return 1;
              }
              if (bInitialPose !== 'sit' && aInitialPose === 'sit') {
                return -1;
              }
              return 0;
            }
            if (aInitialPose === 'sit' || bInitialPose === 'sit') {
              if (aInitialPose !== 'sit') {
                return 1;
              }
              if (bInitialPose !== 'sit') {
                return -1;
              }
              return 0;
            }
            return 0;
          });
        }),
      )
      .pipe(
        tap((list) => {
          if (short_version == false) this.exercises_list.next(list);
        }),
      );
  }
  updatePersonalInfo(reqBody: any) {
    return this.http.fetch('update_user_details', reqBody).pipe();
  }
  deleteUser(reqBody) {
    return this.http.fetch('v3/delete_user', reqBody).pipe();
  }
  getUserTasks(reqBody) {
    return this.http.fetch('get_user_tasks', reqBody).pipe();
  }
  createTask(reqBody) {
    return this.http.fetch('add_new_task', reqBody).pipe();
  }
  updateTask(reqBody) {
    return this.http.fetch('update_task', reqBody).pipe();
  }
  deleteTask(reqBody) {
    return this.http.fetch('delete_task', reqBody).pipe();
  }
  changeUserLanguage(reqBody) {
    return this.http.fetch('change_language', reqBody).pipe();
  }
  changePatientAccessLevel(reqBody) {
    return this.http.fetch('v2/switch_access_level', reqBody).pipe();
  }
  getSurveyResultsOverview(reqBody) {
    return this.http.fetch('get_survey_results_by_time', reqBody).pipe();
  }
  getStepCountsByRange(reqBody) {
    return this.http
      .fetch('get_patient_step_count_by_date_range', reqBody)
      .pipe();
  }
  getOnBoarding(options: { disease_options: string[] }) {
    return this.http
      .fetch('get_onboarding', { ...options })
      .pipe(map((res) => res.data));
  }
  getDiseaseOptions(opt: { language: string; screen: string }) {
    return this.http
      .fetch('get_disease_options', { ...opt })
      .pipe(map((res) => res.data))
      .pipe(
        tap((data) => {
          this.diseasesList$.next(data);
        }),
      );
  }
  getPatientOnBoarding(id: string = null) {
    return this.http
      .fetch('v2/get_patient_onboarding_form', { patient_uid: id })
      .pipe(map((res) => res.data));
  }

  getPatientExerciseOverview(reqBody) {
    return this.http
      .fetch('get_patient_exercise_performance_overview', reqBody)
      .pipe(
        map((res) => res?.data),
        catchError((err) => {
          return of({});
        }),
      );
  }
  getActiveDays(patientId: string) {
    const all = this.patients_active_days.getValue();
    if (all[patientId]) {
      return of(all[patientId]);
    }
    const reqBody = {
      patient_uid: patientId,
      current_date: getDateInString(new Date()),
    };
    return this.http
      .fetch('get_active_days', reqBody)
      .pipe(map((res) => res?.data))
      .pipe(
        tap((data) => {
          const all = this.patients_active_days.getValue();
          all[patientId] = data;
          this.patients_active_days.next({ ...all });
        }),
      );
  }
  getLongestStreak(patientId: string) {
    const all = this.patients_longest_streak.getValue();
    if (all[patientId]) {
      return of(all[patientId]);
    }
    const reqBody = {
      patient_uid: patientId,
      current_date: getDateInString(new Date()),
    };
    return this.http
      .fetch('get_streak', reqBody)
      .pipe(map((res) => res?.data))
      .pipe(
        tap((data) => {
          all[patientId] = data;
          this.patients_longest_streak.next({ ...all });
        }),
      );
  }
  getAppointments(reqBody) {
    return this.http
      .fetch('/v3/retrieve_appointment_data', reqBody)
      .pipe(map((res) => res?.data))
      .pipe(
        map((aps) => {
          return aps
            ? aps
                .map((a) => {
                  return { ...a, date: new Date(a.appointment_dt) };
                })
                .map((a) => {
                  return { ...a, time: this.getLocalTimeString(a.date) };
                })
                .sort((a, b) => {
                  if (Number(a.time.slice(0, 2)) > Number(b.time.slice(0, 2)))
                    return 1;
                  return -1;
                })
            : [];
        }),
      );
  }

  getLocalTimeString(date) {
    const minutes =
      date.getMinutes() < 10 ? '0' + date.getMinutes() : date.getMinutes();
    const hours =
      date.getHours() < 10 ? '0' + date.getHours() : date.getHours();
    return hours + ':' + minutes;
  }

  createAppoinment(reqBody) {
    return this.http.fetch('create_appointment', reqBody).pipe();
  }

  updateAppointment(reqBody) {
    return this.http.fetch('update_appointment', reqBody).pipe();
  }

  deleteAppointment(reqBody) {
    return this.http.fetch('delete_appointment', reqBody).pipe();
  }

  clearSelectedExercise() {
    this._physio_dashboard_data$.next({
      ...this._physio_dashboard_data$.value,
      selectedExercise: null,
    });
  }

  getExerciseCategory(exerciseId: string) {
    if (this.exercises_list.getValue()?.length > 0) {
      const exercises = this.exercises_list.getValue();
      for (let i = 0; i < this.exercises_list.getValue().length; i++) {
        if (exercises[i]._id === exerciseId) {
          return exercises[i].category;
        }
      }
      return '';
    }
  }
}
